import { useEffect, useRef } from "react";

import type { CheckoutSession } from "@/types";
import { CheckoutStatus, CHECKOUT_STATUS_PROCESSING_PAYMENT } from "@/types";
import { emitSlowPaymentLoadingTracking } from "@/shared/tracking/analytics";

/**
 * Fires telemetry when a payment stays in processing state too long.
 *
 * Product rules: first event fires after 60 seconds of continuous processing,
 * then repeats every 30 seconds while the session remains in a processing status.
 * Cleans up automatically when the session transitions out of processing.
 */
export function useSlowPaymentTracking(session?: CheckoutSession): void {
  const paymentLoadingStartTimeRef = useRef<number | null>(null);
  const slowPaymentLoadingIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    const isProcessingPayment = CHECKOUT_STATUS_PROCESSING_PAYMENT.includes(
      session?.status ?? CheckoutStatus.UNKNOWN,
    );

    if (!isProcessingPayment || !session) {
      if (slowPaymentLoadingIntervalRef.current !== null) {
        clearInterval(slowPaymentLoadingIntervalRef.current);
        slowPaymentLoadingIntervalRef.current = null;
      }
      paymentLoadingStartTimeRef.current = null;
      return;
    }

    if (paymentLoadingStartTimeRef.current === null) {
      paymentLoadingStartTimeRef.current = Date.now();
    }

    const startTime = paymentLoadingStartTimeRef.current;
    const sessionRef = session;

    const clearSlowPaymentInterval = (): void => {
      if (slowPaymentLoadingIntervalRef.current === null) return;
      clearInterval(slowPaymentLoadingIntervalRef.current);
      slowPaymentLoadingIntervalRef.current = null;
    };

    const checkSlowLoading = (): void => {
      const stillProcessing = CHECKOUT_STATUS_PROCESSING_PAYMENT.includes(
        sessionRef?.status ?? CheckoutStatus.UNKNOWN,
      );

      if (!stillProcessing || !sessionRef) {
        clearSlowPaymentInterval();
        return;
      }

      const elapsedTimeMs = Date.now() - startTime;
      const elapsedTimeSeconds = Math.floor(elapsedTimeMs / 1000);

      if (elapsedTimeSeconds < 60) return;

      emitSlowPaymentLoadingTracking(sessionRef, elapsedTimeMs);

      if (slowPaymentLoadingIntervalRef.current !== null) return;

      slowPaymentLoadingIntervalRef.current = window.setInterval(() => {
        const currentStillProcessing =
          CHECKOUT_STATUS_PROCESSING_PAYMENT.includes(
            sessionRef?.status ?? CheckoutStatus.UNKNOWN,
          );

        if (!currentStillProcessing || !sessionRef) {
          clearSlowPaymentInterval();
          return;
        }

        const currentElapsedTimeMs = Date.now() - startTime;
        emitSlowPaymentLoadingTracking(sessionRef, currentElapsedTimeMs);
      }, 30000);
    };

    checkSlowLoading();
    const checkInterval = setInterval(checkSlowLoading, 1000);

    return () => {
      clearInterval(checkInterval);
      clearSlowPaymentInterval();
    };
  }, [session?.status, session]);
}
